import React, { useMemo, useEffect } from 'react';
import { Grid, CircularProgress, Card, CardContent, Typography } from '@mui/material';
import TaskCard from '../TaskCard/TaskCard';
import styles from './PlannerTasksBoard.module.css';
import { clearProfilePictureMemoryCache } from '../../redux/slices/dashboard';

const API_URL = process.env.REACT_APP_API_URL;

const PlannerTasksBoard = ({ tasks, loading, error, userEmail, personalMode = true }) => {
  // Clear profile picture cache on mount (fresh page load = fresh data)
  useEffect(() => {
    clearProfilePictureMemoryCache();
  }, []);
  // Parse and categorize tasks
  const { overdueThisWeek, thisWeekByDay, unscheduled, nextWeekByDay } = useMemo(() => {
    const jsonData = tasks || {};

    const parseDate = (dateString) => {
      if (!dateString) return null;
      const parts = dateString.split('/');
      if (parts.length !== 3) return null;
      let yearNum = parseInt(parts[2], 10);
      if (parts[2].length === 2) {
        yearNum = yearNum >= 70 ? 1900 + yearNum : 2000 + yearNum;
      }
      return new Date(yearNum, parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
    };

    const mapForCard = (list) => (Array.isArray(list) ? list.map((t) => {
      const rawName = t.taskName || '';
      const timeMatch = rawName.match(/\[(\d+(?:\.\d+)?)\s*HRS?\]/i);
      const estimatedHours = timeMatch ? parseFloat(timeMatch[1]) : null;
      const cleanedName = rawName.replace(/\[(\d+(?:\.\d+)?)\s*HRS?\]/i, '').replace(/\s*-\s*$/, '').trim();

      const assignedTo = Array.isArray(t.assignedTo) ? t.assignedTo.filter(assignee => assignee && typeof assignee === 'object') : [];
      const primaryAssignee = assignedTo[0];
      const assigneeEmail = primaryAssignee?.email || '';

      return {
        ...t,
        id: t.id || t.taskId || '',
        taskTitle: t.taskTitle || t.taskName || cleanedName,
        clientName: cleanedName || t.clientName || '',
        address: t.address || '',
        deadlineDate: t.deadlineDate || '',
        startDate: t.startDate || '',
        projectNumber: t.projectNumber || t.jobNumber || '',
        deadlineID: t.deadlineID || '',
        deadlineType: t.deadlineType || '',
        assigneeID: t.assigneeID || primaryAssignee?.email || null,
        sharepointFolderLink: t.sharepointFolderLink || '',
        estimatedHours,
        completed: t.completed === true || t.completed === 1 || t.completed === '1' || t.completed === 'true',
        taskProfilePicUrl: assigneeEmail ?
          `${API_URL}/api/bid/opportunity/profile/pic/${encodeURIComponent(assigneeEmail)}` :
          `${API_URL}/api/bid/opportunity/profile/pic/${encodeURIComponent(userEmail || '')}`,
        assignedTo: assignedTo,
        primaryAssignee: primaryAssignee,
      };
    }) : []);

    // Get current week boundaries (Mon-Fri)
    const now = new Date();
    const currentWeekMonday = new Date(now);
    const day = currentWeekMonday.getDay();
    const diffToMonday = (day === 0 ? -6 : 1 - day);
    currentWeekMonday.setHours(0, 0, 0, 0);
    currentWeekMonday.setDate(currentWeekMonday.getDate() + diffToMonday);

    const currentWeekFriday = new Date(currentWeekMonday);
    currentWeekFriday.setDate(currentWeekMonday.getDate() + 4);
    currentWeekFriday.setHours(23, 59, 59, 999);

    // Next week boundaries
    const nextWeekMonday = new Date(currentWeekMonday);
    nextWeekMonday.setDate(currentWeekMonday.getDate() + 7);
    const nextWeekFriday = new Date(nextWeekMonday);
    nextWeekFriday.setDate(nextWeekMonday.getDate() + 4);
    nextWeekFriday.setHours(23, 59, 59, 999);

    const sortByTimeThenDate = (a, b) => {
      const aCompleted = a.completed === true;
      const bCompleted = b.completed === true;
      if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
      const ah = (typeof a.estimatedHours === 'number') ? a.estimatedHours : -1;
      const bh = (typeof b.estimatedHours === 'number') ? b.estimatedHours : -1;
      if (ah !== bh) return bh - ah;
      const ad = parseDate(a.deadlineDate) || new Date(8640000000000000);
      const bd = parseDate(b.deadlineDate) || new Date(8640000000000000);
      return ad - bd;
    };

    // Get day index (1=Mon, 5=Fri) for a date relative to a week's Monday
    const getDayIndex = (date, weekMonday) => {
      if (!date) return null;
      const diffDays = Math.floor((date - weekMonday) / (1000 * 60 * 60 * 24));
      if (diffDays >= 0 && diffDays <= 4) return diffDays + 1; // 1-5 for Mon-Fri
      return null;
    };

    // Combine all tasks from API
    const allOverdue = mapForCard(jsonData.overdue || []);
    const allThisWeek = mapForCard(jsonData.thisWeek || []);
    const allNextWeek = mapForCard(jsonData.nextWeek || []);
    const allTasks = [...allOverdue, ...allThisWeek, ...allNextWeek];

    // Categorize for Block 1 (Current Week)
    const overdueList = [];
    const thisWeekDays = { 1: [], 2: [], 3: [], 4: [], 5: [] };

    // Categorize for Block 2 (Next Week)
    const unscheduledList = [];
    const nextWeekDays = { 1: [], 2: [], 3: [], 4: [], 5: [] };

    allTasks.forEach(task => {
      const dueDate = parseDate(task.deadlineDate);

      if (!dueDate) {
        // No due date = unscheduled
        unscheduledList.push(task);
      } else if (dueDate < currentWeekMonday) {
        // Before this week = overdue
        overdueList.push(task);
      } else if (dueDate <= currentWeekFriday) {
        // This week
        const dayIdx = getDayIndex(dueDate, currentWeekMonday);
        if (dayIdx && thisWeekDays[dayIdx]) {
          thisWeekDays[dayIdx].push(task);
        }
      } else if (dueDate <= nextWeekFriday) {
        // Next week
        const dayIdx = getDayIndex(dueDate, nextWeekMonday);
        if (dayIdx && nextWeekDays[dayIdx]) {
          nextWeekDays[dayIdx].push(task);
        }
      } else {
        // Beyond next week = unscheduled (future)
        unscheduledList.push(task);
      }
    });

    // Sort all lists
    overdueList.sort(sortByTimeThenDate);
    unscheduledList.sort(sortByTimeThenDate);
    for (let d = 1; d <= 5; d++) {
      thisWeekDays[d].sort(sortByTimeThenDate);
      nextWeekDays[d].sort(sortByTimeThenDate);
    }

    return {
      overdueThisWeek: overdueList,
      thisWeekByDay: thisWeekDays,
      unscheduled: unscheduledList,
      nextWeekByDay: nextWeekDays,
    };
  }, [tasks, userEmail]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <CircularProgress size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: 'red' }}>
        {error}
      </div>
    );
  }

  const colGap = 12;
  const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  const renderColumn = (tasks, emptyMessage = 'No Tasks') => (
    <div className={styles.scrollableColumn}>
      {tasks && tasks.length > 0 ? tasks.map(task => (
        <TaskCard
          key={task.id}
          task={task}
          personalMode={personalMode}
          boardFullWidth={true}
          compact={true}
          truncateTitleOneLine={true}
          titleMaxWidth={168}
          largeAvatar={true}
        />
      )) : (
        <Card className={styles.Card}>
          <CardContent className={styles.CardContent}>
            <Typography className={styles.cardTitle}>{emptyMessage}</Typography>
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <div className={styles.tasksBoardContainer}>
      {/* Block 1: Current Week */}
      <div className={styles.weekBlock}>
        <div className={styles.oppHead}>This Week</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Header row */}
          <div style={{ display: 'flex', gap: colGap }}>
            <div style={{ flex: 1, fontWeight: 600, paddingLeft: 4, textAlign: 'left', fontFamily: 'PT Sans' }}>Overdue</div>
            {dayLabels.map(d => (
              <div key={d} style={{ flex: 1, fontWeight: 600, paddingLeft: 4, textAlign: 'left', fontFamily: 'PT Sans' }}>{d}</div>
            ))}
          </div>

          {/* Content row */}
          <div style={{ display: 'flex', gap: colGap }}>
            {/* Overdue column */}
            <div style={{ flex: 1 }}>
              {renderColumn(overdueThisWeek, 'No Overdue')}
            </div>
            {/* Mon-Fri columns */}
            {[1, 2, 3, 4, 5].map(d => (
              <div key={d} style={{ flex: 1 }}>
                {renderColumn(thisWeekByDay[d])}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Block 2: Next Week */}
      <div className={styles.weekBlock} style={{ marginTop: 32 }}>
        <div className={styles.oppHead}>Next Week</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Header row */}
          <div style={{ display: 'flex', gap: colGap }}>
            <div style={{ flex: 1, fontWeight: 600, paddingLeft: 4, textAlign: 'left', fontFamily: 'PT Sans' }}>Unscheduled</div>
            {dayLabels.map(d => (
              <div key={d} style={{ flex: 1, fontWeight: 600, paddingLeft: 4, textAlign: 'left', fontFamily: 'PT Sans' }}>{d}</div>
            ))}
          </div>

          {/* Content row */}
          <div style={{ display: 'flex', gap: colGap }}>
            {/* Unscheduled column */}
            <div style={{ flex: 1 }}>
              {renderColumn(unscheduled, 'No Unscheduled')}
            </div>
            {/* Mon-Fri columns */}
            {[1, 2, 3, 4, 5].map(d => (
              <div key={d} style={{ flex: 1 }}>
                {renderColumn(nextWeekByDay[d])}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlannerTasksBoard;
